import type { CharacterId } from "./core/types";

export const GAME_SOUND_EFFECTS = [
  "step",
  "jump",
  "magic-shot",
  "collectible",
  "damage",
  "enemy-hit",
  "enemy-defeat",
  "enemy-shoot",
  "question-open",
  "question-solved",
  "checkpoint",
  "level-complete",
] as const;

export type GameSoundEffect = (typeof GAME_SOUND_EFFECTS)[number];

export interface GameAudioOptions {
  /** Primarily useful for deterministic tests; the default uses Web Audio. */
  readonly contextFactory?: () => AudioContext | null;
  readonly setInterval?: typeof globalThis.setInterval;
  readonly clearInterval?: typeof globalThis.clearInterval;
  readonly gestureTarget?: AudioGestureTarget | null;
  readonly musicVolume?: number;
  readonly effectsVolume?: number;
}

interface AudioGestureTarget {
  addEventListener(type: string, listener: EventListener, options?: boolean | AddEventListenerOptions): void;
  removeEventListener(type: string, listener: EventListener, options?: boolean | EventListenerOptions): void;
}

interface ThemeDefinition {
  readonly melody: readonly (number | null)[];
  readonly bass: readonly number[];
  readonly wave: OscillatorType;
  readonly beatSeconds: number;
}

interface ToneDefinition {
  readonly frequency: number;
  readonly endFrequency?: number;
  readonly startsAt: number;
  readonly duration: number;
  readonly volume: number;
  readonly wave?: OscillatorType;
  readonly attack?: number;
}

// Two small original pentatonic motifs. They are synthesized in the browser,
// so the game ships no recordings, vocals, licences, or network requests.
// Each theme is a 16-step A/B phrase: the first 8 steps state the motif and
// the second 8 answer it, so the loop repeats half as often as before.
const THEMES: Readonly<Record<CharacterId, ThemeDefinition>> = {
  dragon: {
    melody: [
      261.63, null, 329.63, 392, 329.63, 293.66, 349.23, 392,
      440, 392, 349.23, null, 293.66, 329.63, 261.63, null,
    ],
    bass: [130.81, 146.83, 110, 123.47],
    wave: "triangle",
    beatSeconds: 0.36,
  },
  unicorn: {
    melody: [
      587.33, 739.99, 880, null, 987.77, 880, 739.99, 659.25,
      880, 987.77, 1174.66, null, 987.77, 880, 659.25, 587.33,
    ],
    bass: [293.66, 369.99, 440, 329.63],
    wave: "sine",
    beatSeconds: 0.32,
  },
};

const MUSIC_LOOKAHEAD_SECONDS = 0.28;
const MUSIC_SCHEDULER_INTERVAL_MS = 90;
const MIN_GAIN = 0.0001;
const DEFAULT_MUSIC_VOLUME = 0.58;
const DEFAULT_EFFECTS_VOLUME = 0.9;
const PENDING_EFFECT_LIFETIME_MS = 900;
const MAX_PENDING_EFFECTS = 8;

function clampVolume(value: number, fallback: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;
}

function defaultContextFactory(): AudioContext | null {
  if (typeof globalThis === "undefined") return null;
  const audioGlobal = globalThis as typeof globalThis & {
    readonly AudioContext?: typeof AudioContext;
    readonly webkitAudioContext?: typeof AudioContext;
  };
  const Context = audioGlobal.AudioContext ?? audioGlobal.webkitAudioContext;
  if (!Context) return null;
  try {
    return new Context();
  } catch {
    return null;
  }
}

function defaultGestureTarget(): AudioGestureTarget | null {
  return typeof window === "undefined" ? null : window;
}

/**
 * Procedural music and sound effects for the platform game.
 *
 * `requestMusic` is safe during render/setup and intentionally does not create
 * an AudioContext. Call `resumeFromGesture` directly inside the first click or
 * touch handler; this satisfies browser autoplay rules and starts the requested
 * theme. SFX are silent no-ops until that gesture has unlocked audio.
 */
export class GameAudio {
  private readonly contextFactory: () => AudioContext | null;
  private readonly scheduleInterval: typeof globalThis.setInterval;
  private readonly cancelInterval: typeof globalThis.clearInterval;
  private readonly gestureTarget: AudioGestureTarget | null;

  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private effectsGain: GainNode | null = null;
  private schedulerId: ReturnType<typeof globalThis.setInterval> | null = null;
  private readonly musicOscillators = new Set<OscillatorNode>();
  private readonly effectOscillators = new Set<OscillatorNode>();
  private readonly pendingEffects: Array<{
    effect: GameSoundEffect;
    character: CharacterId;
    requestedAt: number;
  }> = [];

  private character: CharacterId = "unicorn";
  private musicRequested = false;
  private muted = false;
  private musicVolume: number;
  private effectsVolume: number;
  private nextMusicTime = 0;
  private musicStep = 0;
  private recoveryListenersInstalled = false;

  private readonly handleContextStateChange = () => {
    const context = this.context;
    if (!context) return;
    if (context.state === "running") {
      this.finishActivation();
      return;
    }

    // WebKit may report the non-standard `interrupted` state when another
    // audio session (including speech synthesis) takes focus. Stop the timer
    // until a real gesture can resume it instead of silently losing all notes.
    if (context.state !== "closed") this.stopScheduler(true);
  };

  private readonly handleRecoveryGesture = () => {
    const context = this.context;
    if (!context || this.muted || context.state === "running" || context.state === "closed") return;
    // Creating and starting a tiny source synchronously inside the gesture is
    // required by older iOS/WebKit releases; resume() alone is not sufficient
    // on all installed-home-screen and in-app browser variants.
    this.primeContext(context);
    void this.activateContext(context);
  };

  constructor(options: GameAudioOptions = {}) {
    this.contextFactory = options.contextFactory ?? defaultContextFactory;
    this.scheduleInterval = options.setInterval ?? globalThis.setInterval.bind(globalThis);
    this.cancelInterval = options.clearInterval ?? globalThis.clearInterval.bind(globalThis);
    this.gestureTarget = options.gestureTarget === undefined ? defaultGestureTarget() : options.gestureTarget;
    this.musicVolume = clampVolume(options.musicVolume ?? DEFAULT_MUSIC_VOLUME, DEFAULT_MUSIC_VOLUME);
    this.effectsVolume = clampVolume(options.effectsVolume ?? DEFAULT_EFFECTS_VOLUME, DEFAULT_EFFECTS_VOLUME);
  }

  /** Selects a theme without trying to bypass autoplay protection. */
  requestMusic(character: CharacterId = this.character): void {
    this.musicRequested = true;
    this.setCharacter(character);
    if (this.context?.state === "running" && !this.muted) this.startScheduler();
  }

  /** Changes motifs immediately, cancelling notes queued from the old theme. */
  setCharacter(character: CharacterId): void {
    if (this.character === character) return;
    this.character = character;
    if (!this.context) return;
    this.stopScheduler(true);
    if (this.musicRequested && this.context.state === "running" && !this.muted) {
      this.startScheduler();
    }
  }

  /**
   * Call synchronously from a click, pointer, touch, or keyboard handler.
   * Returns false when Web Audio is unavailable or the browser rejects resume.
   */
  async resumeFromGesture(character?: CharacterId): Promise<boolean> {
    if (character) this.setCharacter(character);
    this.musicRequested = true;
    const context = this.ensureContext();
    if (!context) return false;
    this.primeContext(context);
    return this.activateContext(context);
  }

  pauseMusic(): void {
    this.musicRequested = false;
    this.stopScheduler(true);
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    this.applyOutputLevels();
    if (muted) {
      this.stopScheduler(true);
    } else if (this.musicRequested && this.context?.state === "running") {
      this.startScheduler();
    } else if (this.context && this.context.state !== "closed") {
      // setMuted(false) is normally called by the sound button, so prime and
      // resume while that user activation is still available.
      this.primeContext(this.context);
      void this.activateContext(this.context);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = clampVolume(volume, this.musicVolume);
    this.applyOutputLevels();
  }

  setEffectsVolume(volume: number): void {
    this.effectsVolume = clampVolume(volume, this.effectsVolume);
    this.applyOutputLevels();
  }

  /** Plays a short effect. Before audio is unlocked this is a safe no-op. */
  play(effect: GameSoundEffect, character: CharacterId = this.character): void {
    const context = this.context;
    if (this.muted || !context || context.state === "closed") return;
    if (context.state !== "running" || !this.effectsGain) {
      this.queueEffect(effect, character);
      // This may recover immediately on desktop. If autoplay policy rejects it,
      // the capture-phase gesture listener retries while user activation is live.
      void this.activateContext(context);
      return;
    }
    this.playNow(effect, character);
  }

  private playNow(effect: GameSoundEffect, character: CharacterId): void {
    const context = this.context;
    if (this.muted || !context || context.state !== "running" || !this.effectsGain) return;
    const now = context.currentTime + 0.008;
    for (const tone of this.effectTones(effect, character, now)) {
      this.scheduleTone(tone, this.effectsGain, this.effectOscillators);
    }
  }

  /** Stops every note/timer and closes the context. The instance can be reused. */
  dispose(): void {
    this.musicRequested = false;
    this.stopScheduler(true);
    this.stopOscillators(this.effectOscillators);
    this.pendingEffects.length = 0;
    const context = this.context;
    this.removeRecoveryListeners(context);
    this.context = null;
    this.masterGain = null;
    this.musicGain = null;
    this.effectsGain = null;
    if (context && context.state !== "closed") {
      try {
        void context.close().catch(() => undefined);
      } catch {
        // Cleanup must remain safe in partially implemented Web Audio browsers.
      }
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.context?.state === "closed") {
      this.removeRecoveryListeners(this.context);
      this.context = null;
      this.masterGain = null;
      this.musicGain = null;
      this.effectsGain = null;
    }
    if (this.context) return this.context;

    let context: AudioContext | null = null;
    try {
      context = this.contextFactory();
    } catch {
      return null;
    }
    if (!context) return null;

    try {
      const master = context.createGain();
      const music = context.createGain();
      const effects = context.createGain();
      music.connect(master);
      effects.connect(master);
      master.connect(context.destination);
      this.context = context;
      this.masterGain = master;
      this.musicGain = music;
      this.effectsGain = effects;
      this.applyOutputLevels();
      this.installRecoveryListeners(context);
      return context;
    } catch {
      try {
        void context.close().catch(() => undefined);
      } catch {
        // Ignore cleanup failure after an incomplete Web Audio initialization.
      }
      return null;
    }
  }

  private installRecoveryListeners(context: AudioContext): void {
    if (this.recoveryListenersInstalled) return;
    try {
      context.addEventListener("statechange", this.handleContextStateChange);
      this.gestureTarget?.addEventListener("pointerdown", this.handleRecoveryGesture, true);
      this.gestureTarget?.addEventListener("touchend", this.handleRecoveryGesture, true);
      this.gestureTarget?.addEventListener("keydown", this.handleRecoveryGesture, true);
      this.recoveryListenersInstalled = true;
    } catch {
      // Legacy Web Audio implementations may omit EventTarget methods. The
      // explicit start-button activation still remains available in that case.
    }
  }

  private removeRecoveryListeners(context: AudioContext | null): void {
    if (!this.recoveryListenersInstalled) return;
    try {
      context?.removeEventListener("statechange", this.handleContextStateChange);
      this.gestureTarget?.removeEventListener("pointerdown", this.handleRecoveryGesture, true);
      this.gestureTarget?.removeEventListener("touchend", this.handleRecoveryGesture, true);
      this.gestureTarget?.removeEventListener("keydown", this.handleRecoveryGesture, true);
    } catch {
      // Cleanup is best-effort on older embedded browsers.
    }
    this.recoveryListenersInstalled = false;
  }

  private primeContext(context: AudioContext): void {
    if (context.state === "closed") return;
    try {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      gain.gain.setValueAtTime(MIN_GAIN, context.currentTime);
      oscillator.frequency.setValueAtTime(220, context.currentTime);
      oscillator.connect(gain);
      gain.connect(this.masterGain ?? context.destination);
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
          gain.disconnect();
        } catch {
          // The context may have been disposed before the priming tone ended.
        }
      };
      oscillator.start(context.currentTime);
      oscillator.stop(context.currentTime + 0.025);
    } catch {
      // Priming is a compatibility enhancement, not a gameplay dependency.
    }
  }

  private async activateContext(context: AudioContext): Promise<boolean> {
    if (context !== this.context || context.state === "closed") return false;
    try {
      // Safari/WebKit also exposes `interrupted`, which is intentionally not in
      // TypeScript's cross-browser AudioContextState union. Resume every
      // non-running state rather than handling only the standard `suspended`.
      if (context.state !== "running") await context.resume();
    } catch {
      return false;
    }
    if (context !== this.context || context.state !== "running") return false;
    this.finishActivation();
    return true;
  }

  private finishActivation(): void {
    if (this.context?.state !== "running") return;
    this.applyOutputLevels();
    if (!this.muted) this.startScheduler();
    this.flushPendingEffects();
  }

  private queueEffect(effect: GameSoundEffect, character: CharacterId): void {
    this.pendingEffects.push({ effect, character, requestedAt: Date.now() });
    if (this.pendingEffects.length > MAX_PENDING_EFFECTS) this.pendingEffects.shift();
  }

  private flushPendingEffects(): void {
    if (this.muted || this.context?.state !== "running") return;
    const cutoff = Date.now() - PENDING_EFFECT_LIFETIME_MS;
    const pending = this.pendingEffects.splice(0).filter(({ requestedAt }) => requestedAt >= cutoff);
    for (const { effect, character } of pending) this.playNow(effect, character);
  }

  private applyOutputLevels(): void {
    const context = this.context;
    if (!context) return;
    const now = context.currentTime;
    this.setGain(this.masterGain, this.muted ? 0 : 1, now);
    this.setGain(this.musicGain, this.musicVolume, now);
    this.setGain(this.effectsGain, this.effectsVolume, now);
  }

  private setGain(node: GainNode | null, value: number, time: number): void {
    if (!node) return;
    try {
      node.gain.cancelScheduledValues(time);
      node.gain.setTargetAtTime(Math.max(MIN_GAIN, value), time, 0.018);
      if (value === 0) node.gain.setValueAtTime(0, time + 0.08);
    } catch {
      // Some older browsers expose Web Audio but only a subset of AudioParam.
      try {
        node.gain.value = value;
      } catch {
        // Audio is optional; never break gameplay over an output-level failure.
      }
    }
  }

  private startScheduler(): void {
    const context = this.context;
    if (
      this.schedulerId !== null ||
      !context ||
      context.state !== "running" ||
      !this.musicGain ||
      this.muted ||
      !this.musicRequested
    ) {
      return;
    }
    this.nextMusicTime = context.currentTime + 0.035;
    this.musicStep = 0;
    this.scheduleMusicAhead();
    this.schedulerId = this.scheduleInterval(() => this.scheduleMusicAhead(), MUSIC_SCHEDULER_INTERVAL_MS);
  }

  private stopScheduler(cancelQueuedNotes: boolean): void {
    if (this.schedulerId !== null) {
      this.cancelInterval(this.schedulerId);
      this.schedulerId = null;
    }
    if (cancelQueuedNotes) this.stopOscillators(this.musicOscillators);
  }

  private stopOscillators(nodes: Set<OscillatorNode>): void {
    for (const oscillator of nodes) {
      try {
        oscillator.stop();
      } catch {
        // stop() may throw after a node has naturally ended.
      }
      try {
        oscillator.disconnect();
      } catch {
        // A disconnected node is already clean.
      }
    }
    nodes.clear();
  }

  private scheduleMusicAhead(): void {
    const context = this.context;
    const destination = this.musicGain;
    if (!context || context.state !== "running" || !destination || this.muted || !this.musicRequested) return;
    const theme = THEMES[this.character];
    if (this.nextMusicTime < context.currentTime - theme.beatSeconds) {
      this.nextMusicTime = context.currentTime + 0.035;
    }

    while (this.nextMusicTime < context.currentTime + MUSIC_LOOKAHEAD_SECONDS) {
      const step = this.musicStep % theme.melody.length;
      const frequency = theme.melody[step];
      if (frequency) {
        this.scheduleTone(
          {
            frequency,
            startsAt: this.nextMusicTime,
            duration: theme.beatSeconds * 0.76,
            volume: this.character === "dragon" ? 0.15 : 0.125,
            wave: theme.wave,
            attack: 0.025,
          },
          destination,
          this.musicOscillators,
        );
      }

      if (step % 4 === 0) {
        const bass = theme.bass[Math.floor(step / 4) % theme.bass.length] as number;
        this.scheduleTone(
          {
            frequency: bass,
            startsAt: this.nextMusicTime,
            duration: theme.beatSeconds * 2.7,
            volume: this.character === "dragon" ? 0.09 : 0.072,
            wave: "sine",
            attack: 0.05,
          },
          destination,
          this.musicOscillators,
        );
      }

      // A very quiet bell-like octave makes the unicorn loop sparkle without
      // crowding the playfield sounds.
      if (this.character === "unicorn" && step % 4 === 2 && frequency) {
        this.scheduleTone(
          {
            frequency: frequency * 2,
            startsAt: this.nextMusicTime + 0.025,
            duration: theme.beatSeconds * 0.58,
            volume: 0.045,
            wave: "sine",
            attack: 0.012,
          },
          destination,
          this.musicOscillators,
        );
      }

      this.musicStep += 1;
      this.nextMusicTime += theme.beatSeconds;
    }
  }

  private scheduleTone(
    tone: ToneDefinition,
    destination: AudioNode,
    registry: Set<OscillatorNode>,
  ): void {
    const context = this.context;
    if (!context) return;
    try {
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      const attack = Math.min(tone.duration * 0.35, tone.attack ?? 0.018);
      const endsAt = tone.startsAt + tone.duration;
      oscillator.type = tone.wave ?? "triangle";
      oscillator.frequency.setValueAtTime(tone.frequency, tone.startsAt);
      if (tone.endFrequency) {
        oscillator.frequency.exponentialRampToValueAtTime(
          Math.max(20, tone.endFrequency),
          endsAt - 0.012,
        );
      }
      envelope.gain.setValueAtTime(MIN_GAIN, tone.startsAt);
      envelope.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, tone.volume), tone.startsAt + attack);
      envelope.gain.exponentialRampToValueAtTime(MIN_GAIN, endsAt);
      oscillator.connect(envelope);
      envelope.connect(destination);
      registry.add(oscillator);
      oscillator.onended = () => {
        registry.delete(oscillator);
        try {
          oscillator.disconnect();
          envelope.disconnect();
        } catch {
          // Nodes may already be disconnected by dispose/theme switching.
        }
      };
      oscillator.start(tone.startsAt);
      oscillator.stop(endsAt + 0.015);
    } catch {
      // A sound effect should never be capable of interrupting the game loop.
    }
  }

  private effectTones(
    effect: GameSoundEffect,
    character: CharacterId,
    now: number,
  ): readonly ToneDefinition[] {
    switch (effect) {
      case "step":
        return character === "dragon"
          ? [{ frequency: 145, endFrequency: 112, startsAt: now, duration: 0.075, volume: 0.085, wave: "triangle" }]
          : [{ frequency: 260, endFrequency: 215, startsAt: now, duration: 0.065, volume: 0.068, wave: "sine" }];
      case "jump":
        return [
          { frequency: 330, endFrequency: 590, startsAt: now, duration: 0.17, volume: 0.18, wave: "sine" },
          { frequency: 660, endFrequency: 880, startsAt: now + 0.035, duration: 0.11, volume: 0.055, wave: "triangle" },
        ];
      case "magic-shot":
        return character === "dragon"
          ? [
              { frequency: 510, endFrequency: 175, startsAt: now, duration: 0.24, volume: 0.17, wave: "sawtooth" },
              { frequency: 125, endFrequency: 80, startsAt: now + 0.02, duration: 0.19, volume: 0.09, wave: "sine" },
            ]
          : [659.25, 880, 1174.66].map((frequency, index) => ({
              frequency,
              endFrequency: frequency * 1.12,
              startsAt: now + index * 0.038,
              duration: 0.16,
              volume: 0.115 - index * 0.018,
              wave: "triangle" as const,
            }));
      case "collectible":
        return [784, 1046.5].map((frequency, index) => ({
          frequency,
          startsAt: now + index * 0.07,
          duration: 0.16,
          volume: 0.13,
          wave: "sine" as const,
        }));
      case "damage":
        return [
          { frequency: 230, endFrequency: 105, startsAt: now, duration: 0.26, volume: 0.2, wave: "triangle" },
          { frequency: 155, endFrequency: 92, startsAt: now + 0.045, duration: 0.2, volume: 0.08, wave: "sine" },
        ];
      case "enemy-hit":
        return [{ frequency: 210, endFrequency: 140, startsAt: now, duration: 0.12, volume: 0.12, wave: "triangle" }];
      case "enemy-defeat":
        return [
          { frequency: 520, endFrequency: 180, startsAt: now, duration: 0.28, volume: 0.15, wave: "triangle" },
          { frequency: 1046.5, startsAt: now + 0.06, duration: 0.14, volume: 0.065, wave: "sine" },
        ];
      case "enemy-shoot":
        return [{ frequency: 300, endFrequency: 430, startsAt: now, duration: 0.13, volume: 0.07, wave: "sine" }];
      case "question-open":
        return [523.25, 659.25, 783.99].map((frequency, index) => ({
          frequency,
          startsAt: now + index * 0.065,
          duration: 0.22,
          volume: 0.1,
          wave: "sine" as const,
        }));
      case "question-solved":
        return [659.25, 783.99, 987.77].map((frequency, index) => ({
          frequency,
          startsAt: now + index * 0.085,
          duration: 0.24,
          volume: 0.13,
          wave: "triangle" as const,
        }));
      case "checkpoint":
        return [392, 523.25, 659.25].map((frequency, index) => ({
          frequency,
          startsAt: now + index * 0.095,
          duration: 0.32,
          volume: 0.105,
          wave: "sine" as const,
          attack: 0.028,
        }));
      case "level-complete":
        return [523.25, 659.25, 783.99, 1046.5].map((frequency, index) => ({
          frequency,
          startsAt: now + index * 0.105,
          duration: 0.42,
          volume: 0.15,
          wave: index % 2 === 0 ? "triangle" as const : "sine" as const,
          attack: 0.025,
        }));
    }
  }
}

/** Shared lazy singleton. It creates no AudioContext until resumeFromGesture. */
export const gameAudio = new GameAudio();
